import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  Plus,
  Trash2,
  Pencil,
  Check,
  X as XIcon,
  BellOff,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useLocalStorageState } from "../lib/useLocalStorageState";
import { createAudioContext, startRingtone, RINGTONE_LIST, RINGTONES, DEFAULT_RINGTONE_KEY } from "../lib/alarmAudio";

/**
 * TIME Watch → Utility → Sveglia
 *
 * Persistenza: localStorage "time-watch:alarms".
 * Ringing: overlay a schermo intero con orario, nome, Ferma, Posticipa (+5 min).
 * Notifiche di sistema: se il tab è in background e il permesso è concesso.
 *
 * Nota mobile/background: i browser non permettono di far suonare una sveglia
 * quando la scheda è chiusa. Per un'esperienza completa in background servirebbe
 * un'app nativa o una PWA con service worker + push server (fuori scope).
 */

const STORAGE_KEY = "time-watch:alarms";
const SNOOZE_MINUTES = 5;

const WEEKDAYS = [
  { idx: 1, short: "Lun", long: "Lunedì" },
  { idx: 2, short: "Mar", long: "Martedì" },
  { idx: 3, short: "Mer", long: "Mercoledì" },
  { idx: 4, short: "Gio", long: "Giovedì" },
  { idx: 5, short: "Ven", long: "Venerdì" },
  { idx: 6, short: "Sab", long: "Sabato" },
  { idx: 0, short: "Dom", long: "Domenica" },
];

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const pad2 = (n) => String(n).padStart(2, "0");

const formatTime = (h, m) => `${pad2(h)}:${pad2(m)}`;

const describeRepeat = (repeat) => {
  if (repeat === "once") return "Una volta";
  if (repeat === "daily") return "Tutti i giorni";
  if (Array.isArray(repeat) && repeat.length > 0) {
    if (repeat.length === 7) return "Tutti i giorni";
    // solo weekdays
    const weekdaySet = [1, 2, 3, 4, 5];
    if (
      repeat.length === 5 &&
      weekdaySet.every((d) => repeat.includes(d))
    )
      return "Lun–Ven";
    return WEEKDAYS.filter((d) => repeat.includes(d.idx))
      .map((d) => d.short)
      .join(" · ");
  }
  return "Una volta";
};

// Chiave stringa "YYYY-MM-DDTHH:MM" del "momento" da usare come marcatore
// di ultima esecuzione, in modo da non far ri-suonare la stessa sveglia
// ripetutamente nello stesso minuto.
const currentMinuteKey = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
};

// isDueNow: la sveglia deve suonare adesso?
// Vero se:
// - snoozeUntil è impostato e già passato
// - oppure il minuto corrente coincide con h:m della sveglia, il giorno
//   della settimana rientra nella ripetizione, e la sveglia non è già stata
//   suonata in questo minuto.
const isDueNow = (alarm, now = Date.now()) => {
  if (!alarm.enabled) return false;
  const nowDate = new Date(now);
  if (alarm.snoozeUntil && alarm.snoozeUntil <= now) return true;
  if (nowDate.getHours() !== alarm.hour) return false;
  if (nowDate.getMinutes() !== alarm.minute) return false;
  const dow = nowDate.getDay();
  let dayMatches = false;
  if (alarm.repeat === "daily") dayMatches = true;
  else if (alarm.repeat === "once") dayMatches = !alarm.firedOnce;
  else if (Array.isArray(alarm.repeat))
    dayMatches = alarm.repeat.includes(dow);
  if (!dayMatches) return false;
  const key = currentMinuteKey(nowDate);
  if (alarm.lastFiredKey === key) return false;
  return true;
};

export default function TimeWatchSvegliaPage() {
  const [alarms, setAlarms] = useLocalStorageState(STORAGE_KEY, []);
  const [editorState, setEditorState] = useState(null); // null | { mode, alarm }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [ringingId, setRingingId] = useState(null);

  const audioCtxRef = useRef(null);
  const stopLoopRef = useRef(null);
  const notifyRef = useRef(null);

  // Richiedi permesso notifiche una volta (facoltativo).
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Loop di controllo: 5s. setInterval continua a girare in background
  // (i browser lo throttlano ma non lo sospendono), quindi al rientro
  // in foreground una sveglia scaduta suona con al più ~qualche secondo di ritardo.
  useEffect(() => {
    const check = () => {
      if (ringingId) return; // già in ring
      const now = Date.now();
      for (const a of alarms) {
        if (!a.enabled) continue;
        if (isDueNow(a, now)) {
          startRinging(a);
          return;
        }
      }
    };
    check();
    const iv = setInterval(check, 5000);
    // Rientro in foreground: check immediato.
    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [alarms, ringingId]);

  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = createAudioContext();
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  };

  const startAlarmSound = (ringtoneKey) => {
    const ctx = ensureAudio();
    if (!ctx) return;
    // Suoneria selezionabile per sveglia (default: 'classica' → 3-3-3 sine 880 Hz).
    const key =
      ringtoneKey && RINGTONES[ringtoneKey] ? ringtoneKey : DEFAULT_RINGTONE_KEY;
    stopLoopRef.current = startRingtone(ctx, key);
  };

  const stopAlarmSound = () => {
    if (stopLoopRef.current) {
      stopLoopRef.current();
      stopLoopRef.current = null;
    }
  };

  const showSystemNotification = (alarm) => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted" ||
      document.visibilityState === "visible"
    )
      return;
    try {
      const n = new Notification("Sveglia", {
        body: `${formatTime(alarm.hour, alarm.minute)}${
          alarm.name ? " · " + alarm.name : ""
        }`,
        tag: `alarm-${alarm.id}`,
        renotify: true,
      });
      notifyRef.current = n;
    } catch {
      // ignore
    }
  };

  const startRinging = (alarm) => {
    setRingingId(alarm.id);
    const key = currentMinuteKey();
    setAlarms((prev) =>
      prev.map((a) => {
        if (a.id !== alarm.id) return a;
        const patch = {
          ...a,
          lastFiredKey: key,
          snoozeUntil: null,
        };
        if (a.repeat === "once") patch.firedOnce = true;
        return patch;
      })
    );
    startAlarmSound(alarm.ringtone);
    showSystemNotification(alarm);
  };

  const stopRinging = (disable = false) => {
    stopAlarmSound();
    if (notifyRef.current) {
      try {
        notifyRef.current.close();
      } catch {
        /* ignore */
      }
      notifyRef.current = null;
    }
    if (disable && ringingId) {
      // sveglia "once": già disattivata via firedOnce, ma togliamo enabled per pulizia UI
      setAlarms((prev) =>
        prev.map((a) => {
          if (a.id !== ringingId) return a;
          if (a.repeat === "once") return { ...a, enabled: false };
          return a;
        })
      );
    }
    setRingingId(null);
  };

  const snoozeRinging = () => {
    if (!ringingId) return;
    const until = Date.now() + SNOOZE_MINUTES * 60 * 1000;
    setAlarms((prev) =>
      prev.map((a) =>
        a.id === ringingId
          ? {
              ...a,
              // se once ed era firedOnce, riattiviamola momentaneamente
              enabled: true,
              firedOnce: false,
              snoozeUntil: until,
            }
          : a
      )
    );
    stopAlarmSound();
    if (notifyRef.current) {
      try {
        notifyRef.current.close();
      } catch {
        /* ignore */
      }
      notifyRef.current = null;
    }
    setRingingId(null);
  };

  const openNewEditor = () => {
    setEditorState({
      mode: "new",
      draft: {
        id: uid(),
        name: "",
        hour: 7,
        minute: 30,
        repeat: "once",
        enabled: true,
        snoozeUntil: null,
        firedOnce: false,
        ringtone: DEFAULT_RINGTONE_KEY,
        createdAt: new Date().toISOString(),
      },
    });
  };

  const openEditEditor = (alarm) => {
    // Fallback per sveglie salvate prima dell'introduzione delle suonerie.
    setEditorState({
      mode: "edit",
      draft: { ringtone: DEFAULT_RINGTONE_KEY, ...alarm },
    });
  };

  const saveEditor = () => {
    if (!editorState) return;
    const d = editorState.draft;
    setAlarms((prev) => {
      const exists = prev.some((a) => a.id === d.id);
      if (exists) return prev.map((a) => (a.id === d.id ? d : a));
      return [d, ...prev];
    });
    setEditorState(null);
  };

  const cancelEditor = () => setEditorState(null);

  const toggleEnabled = (id) => {
    setAlarms((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              enabled: !a.enabled,
              firedOnce: false,
              snoozeUntil: null,
            }
          : a
      )
    );
  };

  const deleteAlarm = (id) => {
    setAlarms((prev) => prev.filter((a) => a.id !== id));
    setConfirmDeleteId(null);
  };

  const sortedAlarms = useMemo(() => {
    return [...alarms].sort((a, b) => {
      const ka = a.hour * 60 + a.minute;
      const kb = b.hour * 60 + b.minute;
      return ka - kb;
    });
  }, [alarms]);

  const ringingAlarm = ringingId
    ? alarms.find((a) => a.id === ringingId)
    : null;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-3xl mx-auto">
      <Link
        to="/innovazione/time-watch/utility"
        data-testid="time-watch-sveglia-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Utility
      </Link>

      <PageHeader
        eyebrow="Utility"
        title="Sveglia"
        description="Imposta una o più sveglie con ripetizione personalizzata."
        testId="time-watch-sveglia-header"
      />

      <div className="flex items-center justify-between mb-6">
        <div className="text-[10px] tracking-[0.32em] uppercase text-white/40">
          Elenco ({alarms.length})
        </div>
        <button
          type="button"
          onClick={openNewEditor}
          data-testid="sveglia-new-button"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/90 text-black text-xs tracking-wide font-medium hover:bg-emerald-400 transition"
        >
          <Plus className="w-4 h-4" />
          Nuova Sveglia
        </button>
      </div>

      {sortedAlarms.length === 0 && !editorState && (
        <div
          data-testid="sveglia-empty"
          className="text-white/40 font-light py-8 text-center"
        >
          Nessuna sveglia. Premi &laquo;+ Nuova Sveglia&raquo;.
        </div>
      )}

      <ul data-testid="sveglia-list" className="space-y-3">
        {sortedAlarms.map((a) => (
          <li
            key={a.id}
            data-testid={`sveglia-item-${a.id}`}
            className={`glass-card p-4 sm:p-5 flex items-center gap-4 transition ${
              a.enabled ? "" : "opacity-50"
            }`}
          >
            <button
              type="button"
              onClick={() => openEditEditor(a)}
              data-testid={`sveglia-open-${a.id}`}
              className="flex-1 min-w-0 text-left"
            >
              <div className="text-3xl sm:text-4xl font-extralight text-white tabular-nums leading-none">
                {formatTime(a.hour, a.minute)}
              </div>
              <div className="mt-2 text-[11px] text-white/50">
                {a.name ? (
                  <span className="text-white/70">{a.name}</span>
                ) : (
                  <span className="italic text-white/30">senza nome</span>
                )}
                <span className="mx-2 text-white/20">·</span>
                {describeRepeat(a.repeat)}
              </div>
            </button>
            <ToggleSwitch
              on={a.enabled}
              onChange={() => toggleEnabled(a.id)}
              testId={`sveglia-toggle-${a.id}`}
            />
            <button
              type="button"
              onClick={() => openEditEditor(a)}
              data-testid={`sveglia-edit-${a.id}`}
              aria-label="Modifica"
              className="p-2 rounded-full text-white/40 hover:text-white hover:bg-white/5 transition"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setConfirmDeleteId(a.id)}
              data-testid={`sveglia-delete-${a.id}`}
              aria-label="Elimina"
              className="p-2 rounded-full text-white/40 hover:text-red-300 hover:bg-white/5 transition"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>

      {/* Editor drawer */}
      {editorState && (
        <AlarmEditor
          state={editorState}
          setState={setEditorState}
          onSave={saveEditor}
          onCancel={cancelEditor}
        />
      )}

      {/* Delete confirm */}
      {confirmDeleteId && (
        <ConfirmDialog
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={() => deleteAlarm(confirmDeleteId)}
        />
      )}

      {/* Ringing overlay */}
      {ringingAlarm && (
        <RingingOverlay
          alarm={ringingAlarm}
          onStop={() => stopRinging(true)}
          onSnooze={snoozeRinging}
        />
      )}
    </div>
  );
}

function ToggleSwitch({ on, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onChange}
      data-testid={testId}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        on ? "bg-emerald-500/80" : "bg-white/15"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
          on ? "translate-x-5" : ""
        }`}
      />
    </button>
  );
}

function AlarmEditor({ state, setState, onSave, onCancel }) {
  const d = state.draft;
  const setDraft = (patch) =>
    setState({ ...state, draft: { ...state.draft, ...patch } });

  // Preview di suoneria: mantiene un AudioContext e un handler di stop.
  // Alla chiusura dell'editor viene fermata automaticamente.
  const previewCtxRef = useRef(null);
  const previewStopRef = useRef(null);
  const previewTimerRef = useRef(null);
  const [previewingKey, setPreviewingKey] = useState(null);

  useEffect(() => {
    return () => {
      // cleanup completo al dismount dell'editor
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      if (previewStopRef.current) {
        try {
          previewStopRef.current();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  const stopPreview = () => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    if (previewStopRef.current) {
      try {
        previewStopRef.current();
      } catch {
        /* ignore */
      }
      previewStopRef.current = null;
    }
    setPreviewingKey(null);
  };

  const playPreview = (key) => {
    // Toggle: se stiamo già ascoltando la stessa suoneria → stop.
    if (previewingKey === key) {
      stopPreview();
      return;
    }
    stopPreview();
    if (!previewCtxRef.current) previewCtxRef.current = createAudioContext();
    const ctx = previewCtxRef.current;
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    previewStopRef.current = startRingtone(ctx, key);
    setPreviewingKey(key);
    // Auto-stop dopo ~4s per non lasciare il suono in loop.
    previewTimerRef.current = setTimeout(() => {
      stopPreview();
    }, 4200);
  };

  const closeEditor = () => {
    stopPreview();
    onCancel();
  };

  const saveEditor = () => {
    stopPreview();
    onSave();
  };

  const toggleDay = (idx) => {
    const cur = Array.isArray(d.repeat) ? d.repeat : [];
    if (cur.includes(idx)) {
      const next = cur.filter((x) => x !== idx);
      setDraft({ repeat: next.length ? next : "once" });
    } else {
      setDraft({ repeat: [...cur, idx].sort((a, b) => a - b) });
    }
  };

  const repeatMode = (() => {
    if (d.repeat === "once") return "once";
    if (d.repeat === "daily") return "daily";
    return "custom";
  })();

  return (
    <div
      data-testid="sveglia-editor"
      className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={closeEditor}
    >
      <div
        className="glass-card w-full max-w-lg p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-300">
            {state.mode === "new" ? "Nuova Sveglia" : "Modifica Sveglia"}
          </div>
          <button
            type="button"
            onClick={closeEditor}
            data-testid="sveglia-editor-close"
            className="p-1 rounded-full text-white/50 hover:text-white"
            aria-label="Chiudi"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Time inputs */}
        <div className="flex items-end justify-center gap-4 mb-8">
          <NumberField
            value={d.hour}
            max={23}
            onChange={(v) => setDraft({ hour: v })}
            label="ore"
            testId="sveglia-editor-hour"
          />
          <span className="pb-8 text-4xl text-white/30 font-extralight">
            :
          </span>
          <NumberField
            value={d.minute}
            max={59}
            onChange={(v) => setDraft({ minute: v })}
            label="min"
            testId="sveglia-editor-minute"
          />
        </div>

        {/* Name */}
        <div className="mb-6">
          <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-2">
            Nome (facoltativo)
          </label>
          <input
            type="text"
            value={d.name}
            onChange={(e) => setDraft({ name: e.target.value })}
            placeholder="Es. Lavoro, Allenamento, Medicina…"
            data-testid="sveglia-editor-name"
            className="w-full bg-transparent border-b border-white/15 focus:border-emerald-400/60 outline-none text-white font-light text-base py-2 placeholder:text-white/25"
          />
        </div>

        {/* Repeat mode */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-3">
            Ripetizione
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { key: "once", label: "Una volta" },
              { key: "daily", label: "Tutti i giorni" },
              { key: "custom", label: "Personalizza" },
            ].map((opt) => (
              <button
                type="button"
                key={opt.key}
                onClick={() => {
                  if (opt.key === "once") setDraft({ repeat: "once" });
                  else if (opt.key === "daily") setDraft({ repeat: "daily" });
                  else
                    setDraft({
                      repeat: Array.isArray(d.repeat) ? d.repeat : [1, 2, 3, 4, 5],
                    });
                }}
                data-testid={`sveglia-editor-repeat-${opt.key}`}
                className={`px-3 py-1.5 rounded-full text-xs tracking-wide transition ${
                  repeatMode === opt.key
                    ? "bg-emerald-500/90 text-black"
                    : "bg-white/5 text-white/60 hover:text-white border border-white/10"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {repeatMode === "custom" && (
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => {
                const active =
                  Array.isArray(d.repeat) && d.repeat.includes(day.idx);
                return (
                  <button
                    type="button"
                    key={day.idx}
                    onClick={() => toggleDay(day.idx)}
                    data-testid={`sveglia-editor-day-${day.idx}`}
                    className={`w-11 h-11 rounded-full text-xs tracking-wide transition ${
                      active
                        ? "bg-emerald-500/90 text-black"
                        : "bg-white/5 text-white/60 hover:text-white border border-white/10"
                    }`}
                  >
                    {day.short}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Suoneria */}
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-3">
            Suoneria
          </div>
          <div
            data-testid="sveglia-editor-ringtones"
            className="grid grid-cols-1 gap-2"
          >
            {RINGTONE_LIST.map((r) => {
              const selected = (d.ringtone || DEFAULT_RINGTONE_KEY) === r.key;
              const playing = previewingKey === r.key;
              return (
                <div
                  key={r.key}
                  data-testid={`sveglia-editor-ringtone-row-${r.key}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition ${
                    selected
                      ? "border-emerald-400/45 bg-emerald-400/5"
                      : "border-white/10 bg-white/[0.02] hover:border-white/25"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setDraft({ ringtone: r.key })}
                    data-testid={`sveglia-editor-ringtone-${r.key}`}
                    className="flex-1 min-w-0 text-left"
                    aria-pressed={selected}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={`w-3.5 h-3.5 rounded-full border transition ${
                          selected
                            ? "border-emerald-300 bg-emerald-400/80"
                            : "border-white/25"
                        }`}
                      />
                      <span className="text-sm font-light text-white">
                        {r.label}
                      </span>
                    </div>
                    <div className="mt-1 ml-6 text-[11px] font-light text-white/45 leading-relaxed">
                      {r.description}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => playPreview(r.key)}
                    data-testid={`sveglia-editor-ringtone-preview-${r.key}`}
                    aria-label={
                      playing
                        ? `Interrompi anteprima ${r.label}`
                        : `Ascolta anteprima ${r.label}`
                    }
                    className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] tracking-[0.24em] uppercase transition ${
                      playing
                        ? "bg-emerald-500/90 text-black"
                        : "bg-white/5 text-white/70 hover:text-white border border-white/10"
                    }`}
                  >
                    {playing ? "Stop" : "Ascolta"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Enabled switch */}
        <div className="flex items-center justify-between mb-8">
          <div className="text-[10px] tracking-[0.32em] uppercase text-white/40">
            Stato
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50">
              {d.enabled ? "Attiva" : "Disattiva"}
            </span>
            <ToggleSwitch
              on={d.enabled}
              onChange={() => setDraft({ enabled: !d.enabled })}
              testId="sveglia-editor-enabled"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={closeEditor}
            data-testid="sveglia-editor-cancel"
            className="px-5 py-2.5 rounded-full text-white/60 hover:text-white text-sm tracking-wide"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={saveEditor}
            data-testid="sveglia-editor-save"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition"
          >
            <Check className="w-4 h-4" />
            Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function NumberField({ value, max, onChange, label, testId }) {
  const handle = (raw) => {
    let n = parseInt(raw, 10);
    if (Number.isNaN(n)) n = 0;
    n = Math.max(0, Math.min(max, n));
    onChange(n);
  };
  return (
    <label className="flex flex-col items-center">
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        onChange={(e) => handle(e.target.value)}
        data-testid={testId}
        className="w-24 sm:w-28 bg-transparent border-b border-white/15 focus:border-emerald-400/60 outline-none text-center font-extralight text-white tabular-nums text-5xl sm:text-6xl leading-none pb-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="mt-3 text-[10px] tracking-[0.32em] uppercase text-white/40">
        {label}
      </span>
    </label>
  );
}

function ConfirmDialog({ onCancel, onConfirm }) {
  return (
    <div
      data-testid="sveglia-confirm-delete"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="glass-card w-full max-w-sm p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] tracking-[0.32em] uppercase text-white/50 mb-3">
          Conferma
        </div>
        <div className="text-white font-light mb-6">
          Vuoi eliminare questa sveglia?
        </div>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid="sveglia-confirm-cancel"
            className="px-5 py-2.5 rounded-full text-white/60 hover:text-white text-sm"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="sveglia-confirm-ok"
            className="px-6 py-2.5 rounded-full bg-red-500/90 text-white text-sm font-medium hover:bg-red-400 transition"
          >
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

function RingingOverlay({ alarm, onStop, onSnooze }) {
  const [nowStr, setNowStr] = useState(() => {
    const d = new Date();
    return formatTime(d.getHours(), d.getMinutes());
  });
  useEffect(() => {
    const iv = setInterval(() => {
      const d = new Date();
      setNowStr(formatTime(d.getHours(), d.getMinutes()));
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div
      data-testid="sveglia-ringing"
      className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-6"
    >
      <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-300 mb-8 animate-pulse">
        Sveglia
      </div>
      <div className="text-[24vw] sm:text-[18vw] lg:text-[14rem] font-extralight text-white leading-none tabular-nums tracking-tight">
        {nowStr}
      </div>
      {alarm.name && (
        <div className="mt-6 text-xl sm:text-2xl font-light text-white/70">
          {alarm.name}
        </div>
      )}
      <div className="mt-16 flex items-center gap-4">
        <button
          type="button"
          onClick={onSnooze}
          data-testid="sveglia-ringing-snooze"
          className="px-8 py-4 rounded-full bg-amber-400 text-black text-sm tracking-wide font-medium hover:bg-amber-300 transition"
        >
          Posticipa {SNOOZE_MINUTES} min
        </button>
        <button
          type="button"
          onClick={onStop}
          data-testid="sveglia-ringing-stop"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-red-500/90 text-white text-sm tracking-wide font-medium hover:bg-red-400 transition"
        >
          <BellOff className="w-4 h-4" />
          Interrompi
        </button>
      </div>
    </div>
  );
}
